import { Button } from 'admin/components/atoms/Buttons';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { usePapaParse } from 'react-papaparse';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const DropZoneTextWrapper = styled.p`
  min-height: 2.5em;
  text-align: center;
`;

const HelpText = styled.p`
  padding: 0 10px;
  color: gray;
`;

const fileupStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: 300,
  padding: 10,
  margin: 10,
};
const borderNormalStyle = {
  border: '1px dotted #888',
};
const borderDragStyle = {
  border: '1px solid #00f',
  transition: 'border .5s ease-in-out',
};


const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ClientIdsFileInputs = ({ onReadCustomerIds, onError, selectedFile, onSelectFile }) => {
  const { readString } = usePapaParse();

  const readContent = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const { result } = event.target;
        resolve(result);
      };
      reader.onerror = () => reject();

      reader.readAsText(file);
    });

  const extractCustomerIds = (csvString) =>
    new Promise((resolve, reject) =>
      readString(csvString, {
        header: true,
        worker: true,
        skipEmptyLines: true,
        complete: (results) => {
          const { data, errors } = results;
          if (errors.length !== 0) {
            reject();
          }
          const customerIds = data.map((customer) => customer.id).filter((id) => UUID_REGEX.test(id));
          resolve(customerIds);
        },
      })
    );

  const onFileSelect = useCallback(async (files) => {
    const file = files[0];

    try {
      const fileContent = await readContent(file);
      const customerIds = await extractCustomerIds(fileContent);
      onReadCustomerIds(customerIds);
      onSelectFile(file);
    } catch (_) {
      onReadCustomerIds([]);
      onSelectFile(null);
      // eslint-disable-next-line no-unused-expressions
      onError?.();
    }
  }, []);

  const onFileDialogCancel = () => {
    onReadCustomerIds([]);
    onSelectFile(null);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: onFileSelect,
    maxFiles: 1,
    accept: '.csv',
    noClick: true,
    onFileDialogCancel,
  });
  const uploadstyle = useMemo(
    () => ({
      ...fileupStyle,
      ...(isDragActive ? borderDragStyle : borderNormalStyle),
    }),
    [isDragActive]
  );

  const dropZoneText = () => {
    if (isDragActive) return 'ドラッグ＆ドロップでファイル指定...';

    if (selectedFile) return selectedFile.name;

    return 'ファイルを選択';
  };

  return (
    <>
      <HelpText>
        *<Link to="/customers">お客様一覧画面</Link>
        からCSVをダウンロードして送金対象のユーザのみを残したものをアップロードして下さい
      </HelpText>
      <div {...getRootProps({ style: uploadstyle })}>
        <input {...getInputProps()} />
        <DropZoneTextWrapper>{dropZoneText()}</DropZoneTextWrapper>
        <Button type="button" onClick={open}>
          CSVファイルを選択
        </Button>
      </div>
    </>
  );
};

ClientIdsFileInputs.propTypes = {
  onReadCustomerIds: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

ClientIdsFileInputs.defaultProps = {
  onError: () => {},
};

export default ClientIdsFileInputs;
